import type { Folder } from '@carboncopyco/types';
import { Button, InputTextField, Loading, Modal } from '@carboncopyco/ui';
import { Form, Formik } from 'formik';
import { object, string } from 'yup';

const formSchema = object({
  folderName: string()
    .required('Field can’t be empty.')
    .matches(
      /^[^/]*$/,
      'Folder names cannot contain forward slashes (/). Please use a different name'
    )
    .trim(),
}).required();

export interface FolderRenameModalProps {
  onClose: () => void;
  name?: string;
  show: boolean;
  activeFoldersNames?: Array<string>;
  archivedFoldersNames?: Array<string>;
  isCreateNew?: boolean;
  onHandleFolderSubmit: (FolderName: Folder['name']) => void;
  isLoading?: boolean;
}

export const FolderModal = ({
  onClose,
  show,
  name,
  activeFoldersNames,
  archivedFoldersNames,
  onHandleFolderSubmit,
  isCreateNew,
  isLoading,
}: FolderRenameModalProps) => {
  const handleSubmit = (
    values: { folderName: string },
    setFieldError: (field: string, errMsg: string) => void
  ) => {
    const trimmedFolderName = values.folderName.trim();
    const isNameExists = activeFoldersNames?.find(
      (itemName) => itemName === trimmedFolderName
    );

    const isNameExistsInArchive = archivedFoldersNames?.find(
      (itemName) => itemName === trimmedFolderName
    );

    isNameExists
      ? setFieldError(
          'folderName',
          'This name is already taken. Please use a different name.'
        )
      : isNameExistsInArchive
        ? setFieldError(
            'folderName',
            'This name is already taken by an archived folder. Please use a different name.'
          )
        : onHandleFolderSubmit(trimmedFolderName);
  };
  return (
    <Formik
      onSubmit={(values, { setFieldError }) => {
        handleSubmit(values, setFieldError);
      }}
      initialValues={{
        folderName: isCreateNew ? '' : name,
      }}
      validationSchema={formSchema}
    >
      {({ values }) => (
        <Modal
          containerClass="w-ds-500"
          open={show}
          onClose={onClose}
          title={isCreateNew ? 'New folder' : 'Rename'}
        >
          <Form>
            <div className="flex flex-col mt-5">
              <InputTextField
                name="folderName"
                label="Folder name"
                autoFocus
              />
              <div className="flex justify-end gap-2 mt-5">
                <Button
                  label="Cancel"
                  secondary
                  link
                  onClick={onClose}
                />
                <Button
                  label={
                    isCreateNew && !isLoading
                      ? 'Create folder'
                      : !isCreateNew && !isLoading
                        ? 'Update'
                        : ''
                  }
                  icon={
                    isLoading ? (
                      <Loading color="text-ds-accent3-100" />
                    ) : undefined
                  }
                  submit
                  disabled={!values.folderName || values.folderName === name}
                />
              </div>
            </div>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
